/**
 * Truy cập dữ liệu của module `tro-ly-ai` (Trợ lý AI, ADR-0086) — port `context.repository.ts` và
 * `assistant.repository.ts` của web-portal native.
 *
 * Đường dẫn: native `/api/context/v1/<x>` và `/api/assistant/<x>` → `/api/v1/m/tro-ly-ai/<x>` (cùng đường con, cùng
 * thân camelCase). Bỏ `X-Tenant-ID`, `X-Chatbot-ID` và mọi tham số tổ chức: tenant đã nằm trong phiên.
 *
 * **Chỉ API thật, không có máy chủ giả** — cùng lý do với `tham-dinh`: backend viết trước giao diện, dựng thêm một tầng
 * mock là tạo ra nguồn sự thật thứ hai phải giữ khớp. Soát bốn trạng thái phải chạy module thật.
 *
 * Vận chuyển riêng (`goi`) thay vì `goiApi` của `lib/api/http.ts` vì một lý do: lỗi của module này mang trường mở rộng
 * `details` của Problem Details — `details.refusals` là danh sách từ chối của bộ canh cổng SQL, mỗi dòng là một câu
 * người đọc SỬA ĐƯỢC. `goiApi` chỉ giữ `errors`, nên đi qua nó là mất đúng phần đáng đọc nhất.
 */
import { getAccessToken } from '../auth/oidc';
import { BoTachKhungSse } from '../api/khung-sse';
import type { ApiError } from '../errors';
import { triThucRepository } from './tri-thuc.repository';
import { vaiTroRepository } from './vai-tro.repository';
import type {
  AppNotification,
  AskBody,
  AskResult,
  AssistantHealth,
  BotGrant,
  BotMaTran,
  CauHinhDem,
  CauHinhDemRes,
  CauHoiDeXuat,
  ChatMessage,
  Chatbot,
  ChatbotDungDuoc,
  ChatbotInput,
  CheDoKhai,
  Conversation,
  CreateSourcePayload,
  DanhSachKhuon,
  DeXuatKhai,
  DinhNghia,
  DoiChieuCheDo,
  GlossaryTerm,
  GraphqlSource,
  HoiThoai,
  HoiThoaiNhatKy,
  IntrospectResult,
  KetQuaChayThu,
  KetQuaDoTim,
  KetQuaGhiPhongVan,
  Khuon,
  KhuonCuaToi,
  KhuonGrant,
  KhuonInput,
  KhuonMaTran,
  LocNhatKyHoiThoai,
  MaTranQuyen,
  NhatKyKhai,
  NhomKhuon,
  PermAccounts,
  PermGrant,
  PhongVanResult,
  ProfileResult,
  QueryResult,
  QueryRun,
  QuestionCandidate,
  QuestionItem,
  QuyenHieuLuc,
  Refusal,
  RunProfileResult,
  SampleItem,
  SqlContext,
  SuDung,
  SuKienHoi,
  SyncRun,
  TestSourceResult,
  TraLoiPhongVan,
  TrangThaiBanSao,
  XoaBangKetQua,
} from '../../modules/tro-ly-ai/types';

export const GOC_TRO_LY = '/api/v1/m/tro-ly-ai';

/**
 * Một lượt hỏi đo được p50≈52s, p95≈83s, trần máy chủ 90s. Ghi hẳn số ra để nó là một quyết định: lần sau ai đó đặt
 * một giá trị "hợp lý" 30s thì luồng hỏi đứt giữa chừng — trông y hệt lỗi backend.
 */
export const ASK_TIMEOUT_MS = 120_000;

/** Trần số lời khai mỗi lượt PUT — khớp `max_length=500` của backend; vượt trần là 422 và mất sạch phần vừa gõ. */
const TOI_DA_MOI_LUOT_KHAI = 500;

/** Mỗi gói lưu bản khai: ERP ~2.800 bảng, gói đầy đủ vài MB, mỗi chặng hạ tầng có trần thân request riêng. */
const MOI_GOI_BANG = 100;

const ma = (s: string) => encodeURIComponent(s);
const nguon = (id: string) => `${GOC_TRO_LY}/replica/sources/${ma(id)}`;
const khuonGoc = (id: string) => `${GOC_TRO_LY}/khuon/sources/${ma(id)}`;
const BOT = `${GOC_TRO_LY}/chatbots`;

/* ------------------------------------------------------------------ *
 * Vận chuyển.
 * ------------------------------------------------------------------ */

type GiaTriQuery = string | number | boolean | undefined | null | readonly string[];

export interface YeuCau {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, GiaTriQuery>;
  body?: unknown;
  signal?: AbortSignal;
}

interface Problem {
  error_code?: string;
  detail?: string;
  errors?: unknown;
  details?: Record<string, unknown>;
  error_msg?: string;
}

/** Mã phía giao diện khi máy chủ trả lời nhưng module/Gateway chưa chạy — cùng mã với `lib/api/http.ts`. */
const API_CHUA_SAN_SANG = 'API_UNAVAILABLE';

function maTheoHttp(status: number): string {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'PERMISSION_DENIED';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 502 && status <= 504) return API_CHUA_SAN_SANG;
  return 'INTERNAL';
}

/**
 * Problem Details → `ApiError`. `details` của `ApiError` mang mảng `errors` (lỗi theo trường, cho `loiTheoTruong`) khi
 * có, ngược lại mang trường mở rộng `details` của module (`{ refusals }`) — xem `layTuChoi`.
 */
async function docLoi(res: Response): Promise<ApiError> {
  let p: Problem | null = null;
  try {
    p = (await res.json()) as Problem;
  } catch {
    if (res.status >= 500) return { code: API_CHUA_SAN_SANG, status: res.status, message: '' };
  }
  if (res.status === 503 && (p?.error_code ?? 'INTERNAL') === 'INTERNAL') {
    return { code: API_CHUA_SAN_SANG, status: res.status, message: '' };
  }
  // 404 do chính APISIX trả (`{"error_msg": ...}`): module chưa được triển khai, không phải "không tìm thấy dữ liệu".
  if (res.status === 404 && !p?.error_code && p?.error_msg) {
    return { code: API_CHUA_SAN_SANG, status: res.status, message: '' };
  }
  return {
    code: p?.error_code ?? maTheoHttp(res.status),
    status: res.status,
    message: p?.detail ?? '',
    details: Array.isArray(p?.errors) ? p?.errors : p?.details,
  };
}

async function datHeader(accept: string, coThan: boolean): Promise<Headers> {
  const h = new Headers({ Accept: accept });
  if (coThan) h.set('Content-Type', 'application/json');
  let token: string | undefined;
  try {
    token = await getAccessToken();
  } catch {
    throw { code: 'UNAUTHENTICATED', status: 401, message: '' } satisfies ApiError;
  }
  if (token) h.set('Authorization', `Bearer ${token}`);
  return h;
}

function dungUrl(duong: string, query: YeuCau['query']): URL {
  const url = new URL(duong, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    // Mảng → lặp tham số (`?nguon=a&nguon=b`), đúng cách FastAPI của native đọc `list[str]`.
    if (Array.isArray(v)) for (const x of v) url.searchParams.append(k, x);
    else url.searchParams.set(k, String(v));
  }
  return url;
}

/**
 * Gọi một đường của module, chuẩn hoá lỗi thành `ApiError` (giữ `details.refusals`). Xuất ra cho repository phụ của
 * từng nhóm màn khi cần một đường chưa có ở `khoTroLy` — vẫn đi qua tầng repository, không gọi thẳng từ hook.
 */
export async function goi<T>(duong: string, yc: YeuCau = {}): Promise<T> {
  const headers = await datHeader('application/json, application/problem+json', yc.body !== undefined);
  let res: Response;
  try {
    res = await fetch(dungUrl(duong, yc.query), {
      method: yc.method ?? 'GET',
      headers,
      body: yc.body === undefined ? undefined : JSON.stringify(yc.body),
      signal: yc.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw { code: 'NETWORK_ERROR', message: '' } satisfies ApiError;
  }
  if (!res.ok) throw await docLoi(res);
  if (res.status === 204) return undefined as T;
  try {
    return (await res.json()) as T;
  } catch {
    // 200 mà không phải JSON: `/api` chưa định tuyến, nhận về `index.html`.
    throw { code: API_CHUA_SAN_SANG, status: res.status, message: '' } satisfies ApiError;
  }
}

/** Danh sách từ chối của bộ canh cổng SQL trong một lỗi bất kỳ (`details.refusals`), hoặc mảng rỗng. */
export function layTuChoi(e: unknown): Refusal[] {
  if (e instanceof LoiHoi) return e.refusals;
  if (e && typeof e === 'object' && 'details' in e) {
    const d = (e as ApiError).details;
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      const r = (d as Record<string, unknown>).refusals;
      if (Array.isArray(r)) return r as Refusal[];
    }
  }
  return [];
}

/** Lỗi trả về TRƯỚC khi luồng hỏi mở — giữ nguyên `refusals` để màn hình vẽ từng dòng sửa được. */
export class LoiHoi extends Error {
  readonly code: string;
  readonly status: number;
  readonly refusals: Refusal[];
  constructor(message: string, code: string, status: number, refusals: Refusal[] = []) {
    super(message);
    this.name = 'LoiHoi';
    this.code = code;
    this.status = status;
    this.refusals = refusals;
  }
}

/**
 * Mở luồng hỏi `POST /ask/stream` và đọc từng sự kiện. Đọc bằng `fetch` + reader như native: `EventSource` không gắn
 * được `Authorization`, và XHR không phát sự kiện dần.
 *
 * Bảy loại sự kiện, loại nằm trong `type` của gói `data` (`step`, `van`, `van_thay`, `tieng`, `done`, `suggestions`,
 * `error`). Không nối lại khi đứt: nối lại một lượt hỏi là hỏi lại từ đầu và tiêu thêm một lượt gọi mô hình.
 *
 * Huỷ bằng `signal` — bấm Dừng hay rời trang thì `fetch` ngắt, máy chủ thấy `close` và dừng lượt gọi mô hình.
 */
export async function* moLuongHoi(body: AskBody, signal: AbortSignal): AsyncGenerator<SuKienHoi> {
  const headers = await datHeader('text/event-stream, application/problem+json', true);
  // Trần thời gian của cả lượt — xem `ASK_TIMEOUT_MS`. Gộp với tín hiệu Dừng của người dùng.
  const henGio = new AbortController();
  const t = setTimeout(() => henGio.abort(), ASK_TIMEOUT_MS);
  const huy = () => henGio.abort();
  signal.addEventListener('abort', huy);
  try {
    let res: Response;
    try {
      res = await fetch(dungUrl(`${GOC_TRO_LY}/ask/stream`, undefined), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: henGio.signal,
        cache: 'no-store',
      });
    } catch (e) {
      if (signal.aborted) throw e;
      throw new LoiHoi('', henGio.signal.aborted ? 'TIMEOUT' : 'NETWORK_ERROR', 0);
    }
    if (!res.ok || !res.body) {
      const loi = await docLoi(res);
      throw new LoiHoi(loi.message, loi.code, loi.status ?? res.status, layTuChoi(loi));
    }
    const doc = res.body.getReader();
    const giaiMa = new TextDecoder();
    const tach = new BoTachKhungSse();
    try {
      for (;;) {
        const { done, value } = await doc.read();
        if (done) break;
        for (const khung of tach.nap(giaiMa.decode(value, { stream: true }))) {
          // Khung không phải JSON (nhịp tim lạ) thì bỏ qua — một gói hỏng không được giết cả luồng.
          let goiTin: unknown;
          try {
            goiTin = JSON.parse(khung.data);
          } catch {
            continue;
          }
          if (goiTin && typeof goiTin === 'object' && typeof (goiTin as SuKienHoi).type === 'string') {
            yield goiTin as SuKienHoi;
          }
        }
      }
    } finally {
      doc.releaseLock();
    }
  } finally {
    clearTimeout(t);
    signal.removeEventListener('abort', huy);
  }
}

/* ------------------------------------------------------------------ *
 * Nguồn, bản sao, tri thức bảng.
 * ------------------------------------------------------------------ */

export const khoTroLy = {
  // ── Nguồn ────────────────────────────────────────────────────────────────
  danhSachNguon: (): Promise<GraphqlSource[]> =>
    goi<{ sources: GraphqlSource[] }>(`${GOC_TRO_LY}/replica/sources`).then((d) => d.sources),

  taoNguon: (du: CreateSourcePayload): Promise<GraphqlSource> =>
    goi(`${GOC_TRO_LY}/replica/sources`, { method: 'POST', body: du }),

  suaNguon: (id: string, du: Record<string, unknown>): Promise<GraphqlSource> =>
    goi(nguon(id), { method: 'PATCH', body: du }),

  /** Xoá kho bản sao cũ của nguồn đã chuyển sang `live`. Nguồn và bản khai giữ nguyên. */
  xoaKhoBanSao: (id: string): Promise<GraphqlSource & { droppedDatabase: string }> =>
    goi(`${nguon(id)}/replica-db`, { method: 'DELETE' }),

  /** `dropData` xoá luôn dữ liệu đã đồng bộ — phá huỷ, màn hình phải hỏi lại trước khi gọi. */
  xoaNguon: (id: string, dropData: boolean): Promise<{ deleted: boolean; droppedDatabase: boolean }> =>
    goi(nguon(id), { method: 'DELETE', query: { drop_data: dropData } }),

  kiemNguon: (id: string): Promise<TestSourceResult> => goi(`${nguon(id)}/test`, { method: 'POST' }),

  dungKho: (id: string, du: Record<string, unknown> = {}): Promise<GraphqlSource> =>
    goi(`${nguon(id)}/provision`, { method: 'POST', body: du }),

  doNguon: (id: string): Promise<IntrospectResult> => goi(`${nguon(id)}/introspect`, { method: 'POST' }),

  // ── Bản sao ──────────────────────────────────────────────────────────────
  trangThai: (id: string): Promise<TrangThaiBanSao> => goi(`${nguon(id)}/status`),

  bangDaKhai: (id: string): Promise<Record<string, unknown>[]> =>
    goi<{ tables: Record<string, unknown>[] }>(`${nguon(id)}/tables`).then((d) => d.tables),

  /**
   * @param giuLoiKhai Gửi ĐỀ XUẤT dò được từ nguồn thì bật, để máy chủ giữ lời khai nghiệp vụ của bảng đã khai. Gửi
   * bản khai người dùng VỪA SỬA thì tắt — ở đó một ô rỗng là lời xoá có chủ ý.
   */
  luuBang: (id: string, tables: Record<string, unknown>[], giuLoiKhai = false): Promise<{ saved: number; created: number }> =>
    goi(`${nguon(id)}/tables`, { method: 'PUT', body: { tables, giuLoiKhai } }),

  /**
   * Lưu bản khai theo NHIỀU GÓI, mỗi gói `MOI_GOI_BANG` bảng. Máy chủ upsert theo `(source, name)` nên n gói cho đúng
   * kết quả của một gói, gói nào hỏng thì phần đã ghi vẫn ở đó. `onTien` để màn hình nói ra tiến độ.
   */
  luuBangTheoGoi: async (
    id: string,
    tables: Record<string, unknown>[],
    giuLoiKhai = false,
    onTien?: (daXong: number, tong: number) => void,
  ): Promise<{ saved: number; created: number }> => {
    let saved = 0;
    let created = 0;
    for (let i = 0; i < tables.length; i += MOI_GOI_BANG) {
      const r = await khoTroLy.luuBang(id, tables.slice(i, i + MOI_GOI_BANG), giuLoiKhai);
      saved += r.saved;
      created += r.created;
      onTien?.(Math.min(i + MOI_GOI_BANG, tables.length), tables.length);
    }
    return { saved, created };
  },

  /** Gỡ MỘT bảng khỏi bản khai (mặc định xoá luôn bảng ở kho bản sao) — đường duy nhất gỡ được bảng. */
  xoaBang: (id: string, bang: string, xoaKho = true): Promise<XoaBangKetQua> =>
    goi(`${nguon(id)}/tables/${ma(bang)}`, { method: 'DELETE', query: { xoa_kho: xoaKho } }),

  dongBo: (id: string, tables?: string[]): Promise<{ sourceId: string; started: boolean; tables: number }> =>
    goi(`${nguon(id)}/sync`, { method: 'POST', body: tables ? { tables } : {} }),

  doTim: (id: string): Promise<KetQuaDoTim> => goi(`${nguon(id)}/analyze`, { method: 'POST' }),

  hoSo: (id: string): Promise<ProfileResult> => goi(`${nguon(id)}/profile`),

  chayHoSo: (id: string): Promise<RunProfileResult> => goi(`${nguon(id)}/profile`, { method: 'POST' }),

  ganTriThuc: (id: string, collectionIds: string[]): Promise<string[]> =>
    goi<{ collectionIds: string[] }>(`${nguon(id)}/knowledge`, { method: 'PUT', body: { collectionIds } }).then(
      (d) => d.collectionIds,
    ),

  thuatNgu: (id: string): Promise<GlossaryTerm[]> =>
    goi<{ glossary: GlossaryTerm[] }>(`${nguon(id)}/glossary`).then((d) => d.glossary),

  /** THAY TRỌN danh sách — thiếu một mục ở đây là XOÁ nó. */
  luuThuatNgu: (id: string, glossary: GlossaryTerm[]): Promise<GlossaryTerm[]> =>
    goi<{ glossary: GlossaryTerm[] }>(`${nguon(id)}/glossary`, { method: 'PUT', body: { glossary } }).then(
      (d) => d.glossary,
    ),

  dinhNghia: (id: string): Promise<DinhNghia[]> =>
    goi<{ definitions: DinhNghia[] }>(`${nguon(id)}/definitions`).then((d) => d.definitions),

  /** THAY TRỌN danh sách. Ba trường nền tảng tự đặt KHÔNG gửi lên — gửi lại `verified: true` là xoá mất phép hạ cờ. */
  luuDinhNghia: (id: string, definitions: DinhNghia[]): Promise<DinhNghia[]> =>
    goi<{ definitions: DinhNghia[] }>(`${nguon(id)}/definitions`, {
      method: 'PUT',
      body: { definitions: definitions.map((d) => ({ term: d.term, sql_expr: d.sql_expr, depends_on: d.depends_on })) },
    }).then((d) => d.definitions),

  // ── Phỏng vấn khai báo ───────────────────────────────────────────────────
  /**
   * @param bang   Bảng cần lấy câu. Bỏ trống thì máy chủ chọn bảng nặng nhất.
   * @param hauQua Lọc theo mức thiệt hại TRƯỚC khi đếm.
   * @param tatCa  Bỏ phân trang — chỉ cho việc đổ-ra-rồi-bỏ (xuất tệp), đừng giữ trong state màn hình.
   */
  phongVan: (
    id: string,
    tuyChon: { bang?: string | null; hauQua?: string | null; tatCa?: boolean } = {},
  ): Promise<PhongVanResult> =>
    goi(`${nguon(id)}/khai/phong-van`, {
      method: 'POST',
      query: { bang: tuyChon.bang ?? undefined, hauQua: tuyChon.hauQua ?? undefined, tatCa: tuyChon.tatCa ? 1 : undefined },
    }),

  /** Trả lời "bảng này có đáng dùng không" cho MỌI bảng chưa ai trả lời. Không đụng bảng đã có người trả lời. */
  phamViHangLoat: (id: string, giaTri: 'co' | 'khong'): Promise<{ daDoi: number }> =>
    goi(`${nguon(id)}/khai/pham-vi-hang-loat`, { method: 'POST', body: { giaTri } }),

  /**
   * Ghi câu trả lời phỏng vấn, chia lô `TOI_DA_MOI_LUOT_KHAI`. TUẦN TỰ, không `Promise.all`: hai lượt ghi song song
   * trên cùng một nguồn chạm cùng những hàng bảng. Lỗi giữa chừng thì lô trước đã ghi và vẫn còn — bấm Lưu lại an toàn.
   */
  ghiPhongVan: async (id: string, traLoi: TraLoiPhongVan[]): Promise<KetQuaGhiPhongVan> => {
    const lo: TraLoiPhongVan[][] = [];
    for (let i = 0; i < traLoi.length; i += TOI_DA_MOI_LUOT_KHAI) lo.push(traLoi.slice(i, i + TOI_DA_MOI_LUOT_KHAI));
    if (lo.length === 0) lo.push([]);
    const daGhi: string[] = [];
    const boQua: string[] = [];
    const deLen: string[] = [];
    for (const phan of lo) {
      const d = await goi<KetQuaGhiPhongVan>(`${nguon(id)}/khai/phong-van`, { method: 'PUT', body: { traLoi: phan } });
      daGhi.push(...(d.daGhi ?? []));
      boQua.push(...(d.boQua ?? []));
      if (d.deLen) deLen.push(...d.deLen);
    }
    return deLen.length ? { daGhi, boQua, deLen } : { daGhi, boQua };
  },

  cauHoi: (id: string): Promise<QuestionItem[]> =>
    goi<{ questions: QuestionItem[] }>(`${nguon(id)}/questions`).then((d) => d.questions),

  sinhCauHoi: (id: string): Promise<QuestionCandidate[]> =>
    goi<{ candidates: QuestionCandidate[] }>(`${nguon(id)}/questions/generate`, { method: 'POST' }).then(
      (d) => d.candidates,
    ),

  luuCauHoi: (id: string, questions: QuestionItem[]): Promise<QuestionItem[]> =>
    goi<{ questions: QuestionItem[] }>(`${nguon(id)}/questions`, { method: 'PUT', body: { questions } }).then(
      (d) => d.questions,
    ),

  // ── Hội thoại của Hỏi đáp AI ─────────────────────────────────────────────
  /** Đoạn hỏi GIAO với tập nguồn đang chọn — không phải trùng khít. */
  hoiThoai: (sourceIds: string[]): Promise<HoiThoai[]> =>
    goi<{ hoiThoai: HoiThoai[] }>(`${GOC_TRO_LY}/replica/hoi-thoai`, { query: { nguon: sourceIds } }).then(
      (d) => d.hoiThoai,
    ),

  /** Mở một đoạn. Đoạn tự mang `scopeIds`; màn hình khôi phục lựa chọn nguồn từ đó. */
  moHoiThoai: (cvId: string): Promise<HoiThoai> => goi(`${GOC_TRO_LY}/replica/hoi-thoai/${ma(cvId)}`),

  taoHoiThoai: (sourceIds: string[]): Promise<HoiThoai> =>
    goi(`${GOC_TRO_LY}/replica/hoi-thoai`, { method: 'POST', body: { nguon: sourceIds } }),

  ghiLuot: (
    cvId: string,
    du: { author: string; text: string; turn?: number; payload?: Record<string, unknown> },
  ): Promise<{ id: string }> => goi(`${GOC_TRO_LY}/replica/hoi-thoai/${ma(cvId)}/luot`, { method: 'POST', body: du }),

  xoaHoiThoai: (cvId: string): Promise<void> =>
    goi(`${GOC_TRO_LY}/replica/hoi-thoai/${ma(cvId)}`, { method: 'DELETE' }),

  // ── Phân quyền dữ liệu ───────────────────────────────────────────────────
  taiKhoanQuyen: (): Promise<PermAccounts> => goi(`${GOC_TRO_LY}/permissions/accounts`),

  maTranQuyen: (sourceId: string): Promise<MaTranQuyen> => goi(`${GOC_TRO_LY}/permissions/sources/${ma(sourceId)}`),

  luuMaTranQuyen: (sourceId: string, grants: PermGrant[]): Promise<void> =>
    goi(`${GOC_TRO_LY}/permissions/sources/${ma(sourceId)}`, { method: 'PUT', body: { grants } }),

  quyenHieuLuc: (forRole?: string): Promise<QuyenHieuLuc> =>
    goi(`${GOC_TRO_LY}/permissions/effective`, { query: { for_role: forRole } }),

  // ── Khuôn nghiệp vụ ──────────────────────────────────────────────────────
  // Mọi đường GHI chỉ quản trị gọi được: một khuôn là câu SQL làm MẪU cho mọi câu trả lời khớp nó.
  danhSachKhuon: (sourceId: string): Promise<DanhSachKhuon> => goi(khuonGoc(sourceId)),

  /** Khuôn CHÍNH người đang đăng nhập dùng được — không trả câu lệnh. */
  khuonCuaToi: (sourceId: string): Promise<KhuonCuaToi> => goi(`${khuonGoc(sourceId)}/cua-toi`),

  luuNhomKhuon: (
    sourceId: string,
    nhom: { id?: string; ten: string; moTa?: string; thuTu?: number },
  ): Promise<NhomKhuon> => {
    const than = { ten: nhom.ten, moTa: nhom.moTa ?? '', thuTu: nhom.thuTu ?? 0 };
    return nhom.id
      ? goi(`${khuonGoc(sourceId)}/nhom/${ma(nhom.id)}`, { method: 'PUT', body: than })
      : goi(`${khuonGoc(sourceId)}/nhom`, { method: 'POST', body: than });
  },

  xoaNhomKhuon: (sourceId: string, nhomId: string): Promise<void> =>
    goi(`${khuonGoc(sourceId)}/nhom/${ma(nhomId)}`, { method: 'DELETE' }),

  /** Tạo hoặc sửa. Đổi câu lệnh là HẠ `daChayThu` — nền tảng làm, không phải màn hình. */
  luuKhuon: (sourceId: string, k: KhuonInput): Promise<Khuon> => {
    const than = {
      nhomId: k.nhomId,
      ten: k.ten,
      moTa: k.moTa ?? '',
      cachGoi: k.cachGoi ?? [],
      cauHoiMau: k.cauHoiMau ?? [],
      kieu: k.kieu ?? 'cau_lenh',
      sql: k.sql ?? '',
      tenThuTuc: k.tenThuTuc ?? '',
      duongDan: k.duongDan ?? '',
      phuongThuc: k.phuongThuc ?? 'GET',
      thamSo: k.thamSo ?? [],
      cot: k.cot ?? [],
      tongHop: k.tongHop ?? {},
      tomTat: k.tomTat ?? '',
      tieuDe: k.tieuDe ?? '',
      dialect: k.dialect ?? null,
    };
    return k.id
      ? goi(`${khuonGoc(sourceId)}/khuon/${ma(k.id)}`, { method: 'PUT', body: than })
      : goi(`${khuonGoc(sourceId)}/khuon`, { method: 'POST', body: than });
  },

  xoaKhuon: (sourceId: string, khuonId: string): Promise<void> =>
    goi(`${khuonGoc(sourceId)}/khuon/${ma(khuonId)}`, { method: 'DELETE' }),

  /** Chạy câu lệnh trên nguồn thật — đường DUY NHẤT bật `daChayThu`. */
  chayThuKhuon: (sourceId: string, khuonId: string, thamSo: Record<string, string> = {}): Promise<KetQuaChayThu> =>
    goi(`${khuonGoc(sourceId)}/khuon/${ma(khuonId)}/chay-thu`, { method: 'POST', body: { thamSo } }),

  xacNhanKhuon: (sourceId: string, khuonId: string): Promise<Khuon> =>
    goi(`${khuonGoc(sourceId)}/khuon/${ma(khuonId)}/xac-nhan`, { method: 'POST' }),

  maTranKhuon: (sourceId: string): Promise<KhuonMaTran> => goi(`${khuonGoc(sourceId)}/quyen`),

  luuMaTranKhuon: (sourceId: string, grants: KhuonGrant[]): Promise<void> =>
    goi(`${khuonGoc(sourceId)}/quyen`, { method: 'PUT', body: { grants } }),

  doiChieuCheDo: (sourceId: string): Promise<DoiChieuCheDo> => goi(`${khuonGoc(sourceId)}/che-do`),

  datCheDo: (sourceId: string, cheDo: CheDoKhai): Promise<void> =>
    goi(`${khuonGoc(sourceId)}/che-do`, { method: 'PUT', body: { cheDo } }),

  // ── Nhật ký & sử dụng ────────────────────────────────────────────────────
  /** Hai nhịp trong MỘT lời gọi: chu kỳ đồng bộ và câu SQL đã qua bộ canh cổng — để nhìn hai trục thời gian cạnh nhau. */
  luotChay: (sourceId?: string, limit = 100): Promise<{ syncRuns: SyncRun[]; queryRuns: QueryRun[] }> =>
    goi(`${GOC_TRO_LY}/replica/runs`, { query: { limit, source_id: sourceId } }),

  /** Lịch sử trò chuyện XUYÊN vai — chỉ quản trị (403 với vai thường). */
  nhatKyHoiThoai: (loc: LocNhatKyHoiThoai): Promise<HoiThoaiNhatKy[]> =>
    goi<{ hoiThoai: HoiThoaiNhatKy[] }>(`${GOC_TRO_LY}/nhat-ky/hoi-thoai`, {
      query: { ngay: loc.ngay, loai: loc.loai, q: loc.q, limit: loc.limit },
    }).then((d) => d.hoiThoai),

  nhatKyMoHoiThoai: (cvId: string): Promise<HoiThoai> => goi(`${GOC_TRO_LY}/nhat-ky/hoi-thoai/${ma(cvId)}`),

  suDung: (ngay = 7): Promise<SuDung> => goi(`${GOC_TRO_LY}/su-dung`, { query: { ngay } }),

  thongBao: (): Promise<{ unread: number; notifications: AppNotification[] }> =>
    goi(`${GOC_TRO_LY}/replica/notifications`),

  docThongBao: (id?: string): Promise<number> =>
    goi<{ marked: number }>(`${GOC_TRO_LY}/replica/notifications/read`, {
      method: 'POST',
      query: { notification_id: id },
    }).then((d) => d.marked),

  // ── Thử nghiệm ───────────────────────────────────────────────────────────
  goiNguCanh: (sourceId: string, question?: string): Promise<SqlContext> =>
    goi(`${GOC_TRO_LY}/replica/sql-context`, { method: 'POST', body: { sourceId, ...(question ? { question } : {}) } }),

  chayCauLenh: (du: { sourceId: string; sql: string; question?: string; maxRows?: number }): Promise<QueryResult> =>
    goi(`${GOC_TRO_LY}/replica/query`, { method: 'POST', body: du }),

  /** Lưu câu hỏi kèm câu SQL ĐÚNG. Máy chủ chạy qua bộ canh cổng trước khi lưu và trả bản ĐÃ VIẾT LẠI. */
  themCauMau: (v: {
    sourceId: string;
    question: string;
    sql: string;
    generatedSql?: string | null;
    note?: string | null;
  }): Promise<{ id: string; sql: string }> =>
    goi(`${GOC_TRO_LY}/replica/samples`, {
      method: 'POST',
      body: {
        source_id: v.sourceId,
        question: v.question,
        sql: v.sql,
        ...(v.generatedSql ? { generated_sql: v.generatedSql } : {}),
        ...(v.note ? { note: v.note } : {}),
      },
    }),

  cauMau: (sourceId: string): Promise<SampleItem[]> =>
    goi<{ samples: SampleItem[] }>(`${GOC_TRO_LY}/replica/samples`, { query: { source_id: sourceId } }).then(
      (d) => d.samples,
    ),

  /* ------------------------------------------------------------------ *
   * Chatbot.
   * ------------------------------------------------------------------ */

  /** `quanTri` — màn CẤU HÌNH hỏi "tổ chức có những bot nào", kể cả bot chưa cấp cho vai nào (đòi quyền quản trị). */
  danhSachBot: (activeOnly = false, quanTri = false): Promise<Chatbot[]> =>
    goi(BOT, { query: { active_only: activeOnly || undefined, quan_tri: quanTri || undefined } }),

  /** Mọi bot người dùng chat được. */
  botDungDuoc: (): Promise<ChatbotDungDuoc[]> => goi(`${BOT}/dung-duoc`),

  bot: (id: string): Promise<Chatbot> => goi(`${BOT}/${ma(id)}`),

  taoBot: (du: ChatbotInput): Promise<Chatbot> => goi(BOT, { method: 'POST', body: du }),

  suaBot: (id: string, du: Partial<ChatbotInput>): Promise<Chatbot> =>
    goi(`${BOT}/${ma(id)}`, { method: 'PATCH', body: du }),

  xoaBot: (id: string): Promise<void> => goi(`${BOT}/${ma(id)}`, { method: 'DELETE' }),

  /** Cấp khoá nhúng MỚI, làm hỏng mọi đoạn mã đã dán bằng khoá cũ. Không giá trị nào qua `suaBot` đổi được khoá. */
  xoayKhoaNhung: (id: string): Promise<Chatbot> => goi(`${BOT}/${ma(id)}/xoay-khoa-nhung`, { method: 'POST' }),

  maTranBot: (): Promise<BotMaTran> => goi(`${BOT}/quyen`),

  luuMaTranBot: (grants: BotGrant[]): Promise<void> => goi(`${BOT}/quyen`, { method: 'PUT', body: { grants } }),

  // ── Đoạn chat của một bot ────────────────────────────────────────────────
  doanChat: (botId: string, limit = 50): Promise<Conversation[]> =>
    goi(`${BOT}/${ma(botId)}/conversations`, { query: { limit } }),

  moDoan: (botId: string, cvId: string): Promise<Conversation> =>
    goi(`${BOT}/${ma(botId)}/conversations/${ma(cvId)}`),

  taoDoan: (botId: string, title?: string): Promise<Conversation> =>
    goi(`${BOT}/${ma(botId)}/conversations`, { method: 'POST', body: title ? { title } : {} }),

  themTinNhan: (
    botId: string,
    cvId: string,
    du: { author: 'user' | 'bot'; text: string; payload?: Record<string, unknown> },
  ): Promise<ChatMessage> => goi(`${BOT}/${ma(botId)}/conversations/${ma(cvId)}/messages`, { method: 'POST', body: du }),

  xoaDoan: (botId: string, cvId: string): Promise<void> =>
    goi(`${BOT}/${ma(botId)}/conversations/${ma(cvId)}`, { method: 'DELETE' }),

  /* ------------------------------------------------------------------ *
   * Hỏi–đáp (assistant-service của native).
   * ------------------------------------------------------------------ */

  /** Đường không phát dần — cho nơi không cần thấy từng bước. Luồng phát dần: `moLuongHoi`. */
  hoi: (du: AskBody, signal?: AbortSignal): Promise<AskResult> =>
    goi(`${GOC_TRO_LY}/ask`, { method: 'POST', body: du, signal }),

  /** Đọc một câu mẫu bằng giọng đã chọn — WAV base64. Không lưu gì ở máy chủ. */
  ngheThuGiong: (giong: string, van?: string): Promise<string> =>
    goi<{ wavB64: string }>(`${GOC_TRO_LY}/tieng/nghe-thu`, { method: 'POST', body: { giong, ...(van ? { van } : {}) } }).then(
      (d) => d.wavB64,
    ),

  /** Danh mục mô hình và giọng của tầng hỏi–đáp. `/health` gốc là điểm kiểm của module, không phải đường này. */
  sucKhoeTroLy: (): Promise<AssistantHealth> => goi(`${GOC_TRO_LY}/hoi-dap/health`),

  luuCauMauTroLy: (du: {
    sourceId: string;
    question: string;
    sql: string;
    generatedSql?: string;
    note?: string;
  }): Promise<{ id: string }> => goi(`${GOC_TRO_LY}/samples`, { method: 'POST', body: du }),

  /**
   * Tài liệu mô tả nghiệp vụ → ĐỀ XUẤT câu trả lời cho màn phỏng vấn. Không ghi gì. `daXet` là số câu máy chủ THỰC SỰ
   * xem — nó cắt ở trần của mình, màn hình phải nói ra.
   */
  khaiDeXuat: (
    moTa: string,
    cauHoi: CauHoiDeXuat[],
  ): Promise<{ deXuat: DeXuatKhai[]; daXet: number; nhatKy?: NhatKyKhai }> =>
    goi(`${GOC_TRO_LY}/khai/de-xuat`, { method: 'POST', body: { moTa, cauHoi } }),

  cauHinhDem: (): Promise<CauHinhDemRes> => goi(`${GOC_TRO_LY}/cau-hinh-dem`),

  /** Gửi PHẦN đổi. Trả về giá trị máy chủ ĐÃ KẸP — vẽ lại từ đây chứ đừng giữ giá trị vừa gõ. */
  datCauHinhDem: (thay: Partial<CauHinhDem>): Promise<CauHinhDemRes> =>
    goi(`${GOC_TRO_LY}/cau-hinh-dem`, { method: 'PUT', body: thay }),

  /* ------------------------------------------------------------------ *
   * Ngoài module.
   * ------------------------------------------------------------------ */

  /**
   * Bộ tri thức để gắn vào bot/nguồn: `{id, ten}` với `id` là `collection_id` của module tri thức — `knowledgeBaseIds`
   * của native là mã collection. Bộ chưa tạo được collection (`collection_id = null`) không gắn được nên bị bỏ.
   *
   * TODO(ADR-0086): chốt với backend `tro-ly-ai` rằng `knowledgeBaseIds` là `collection_id` hay `id` của bộ tri thức.
   */
  boTriThuc: async (): Promise<{ id: string; ten: string }[]> => {
    const ra: { id: string; ten: string }[] = [];
    let cursor: string | undefined;
    for (let i = 0; i < 20; i += 1) {
      const trang = await triThucRepository.danhSachKho({ limit: 100, cursor });
      for (const kb of trang.items) if (kb.collection_id) ra.push({ id: kb.collection_id, ten: kb.name });
      cursor = trang.next_cursor ?? undefined;
      if (!cursor) break;
    }
    return ra;
  },

  /**
   * Vai trò của tổ chức cho ma trận quyền dữ liệu, quyền khuôn, quyền bot và vai của khung nhúng: module không liệt kê
   * được vai trò, nên lấy từ API vai trò của platform (cùng nguồn màn Vai trò dùng) rồi gửi `code` cho module.
   *
   * `TENANT_ADMIN` là vai lớp 1, không có trong `GET /api/v1/roles` (vai lớp 2), nhưng backend coi nó là vai quản trị —
   * đặt ở đầu để ma trận vẫn khai được cho nó.
   */
  vaiTroToChuc: async (): Promise<{ ma: string; ten: string }[]> => {
    const ra: { ma: string; ten: string }[] = [{ ma: 'TENANT_ADMIN', ten: 'TENANT_ADMIN' }];
    let cursor: string | undefined;
    for (let i = 0; i < 50; i += 1) {
      const trang = await vaiTroRepository.danhSach({ limit: 100, cursor });
      for (const r of trang.items) if (r.code !== 'TENANT_ADMIN') ra.push({ ma: r.code, ten: r.name });
      cursor = trang.next_cursor ?? undefined;
      if (!cursor) break;
    }
    return ra;
  },
};
